<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Chrome } from '@lk77/vue3-color';
import { OnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  ariaLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const isPickerOpen = ref(false);
const triggerRef = ref(null);
const dialogRef = ref(null);
const pickerRef = ref(null);
const uid = useId();
const triggerId = `color-picker-trigger-${uid}`;
const dialogId = `color-picker-dialog-${uid}`;
let previouslyFocusedElement;

const resolvedAriaLabel = computed(() => {
  const label = props.ariaLabel || t('GENERAL.COLOR_PICKER_LABEL');
  return props.modelValue ? `${label}: ${props.modelValue}` : label;
});

const getTriggerElement = () => triggerRef.value?.$el || triggerRef.value;

const getDialogFocusableElements = () => {
  if (!dialogRef.value) return [];
  return [
    ...dialogRef.value.querySelectorAll('button, input, select, [tabindex]'),
  ].filter(element => !element.disabled && element.tabIndex >= 0);
};

const focusPicker = () => {
  (getDialogFocusableElements()[0] || dialogRef.value)?.focus();
};

const openColorPicker = () => {
  if (isPickerOpen.value) return;
  previouslyFocusedElement = document.activeElement;
  isPickerOpen.value = true;
  nextTick(focusPicker);
};

const closeColorPicker = ({ restoreFocus = false } = {}) => {
  if (!isPickerOpen.value) return;
  isPickerOpen.value = false;

  if (restoreFocus) {
    nextTick(() => {
      const focusTarget = previouslyFocusedElement?.isConnected
        ? previouslyFocusedElement
        : getTriggerElement();
      focusTarget?.focus?.();
    });
  }
};

const toggleColorPicker = () => {
  if (isPickerOpen.value) {
    closeColorPicker({ restoreFocus: true });
  } else {
    openColorPicker();
  }
};

const hasConnectedOutsideFocus = element =>
  element instanceof Element &&
  element.isConnected &&
  element !== document.body &&
  element !== document.documentElement &&
  !pickerRef.value?.contains(element);

const closeTogglePicker = () => {
  if (!isPickerOpen.value) return;

  closeColorPicker();
  nextTick(() => {
    if (!hasConnectedOutsideFocus(document.activeElement)) {
      getTriggerElement()?.focus?.();
    }
  });
};

const updateColor = e => {
  emit('update:modelValue', e.hex);
};

const onDialogKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeColorPicker({ restoreFocus: true });
    return;
  }

  if (event.key !== 'Tab') return;

  const focusableElements = getDialogFocusableElements();
  if (!focusableElements.length) {
    event.preventDefault();
    dialogRef.value?.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements.at(-1);
  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

onBeforeUnmount(() => {
  previouslyFocusedElement = undefined;
});

defineExpose({ isPickerOpen, openColorPicker, closeColorPicker });
</script>

<template>
  <div ref="pickerRef" class="relative w-fit">
    <OnClickOutside @trigger="closeTogglePicker">
      <Button
        :id="triggerId"
        ref="triggerRef"
        color="slate"
        icon="i-lucide-pipette"
        trailing-icon
        :aria-label="resolvedAriaLabel"
        :aria-expanded="isPickerOpen"
        aria-haspopup="dialog"
        :aria-controls="dialogId"
        class="!min-h-11 !px-3 !py-3 [&>svg]:w-4 [&>svg]:h-4"
        @click="toggleColorPicker"
      >
        <div class="flex items-center flex-grow gap-2">
          <span
            class="rounded-md size-4"
            :style="{ backgroundColor: modelValue }"
          />
          <span class="min-w-0 truncate">{{ modelValue }}</span>
        </div>
      </Button>
      <div
        v-if="isPickerOpen"
        :id="dialogId"
        ref="dialogRef"
        role="dialog"
        aria-modal="false"
        :aria-labelledby="triggerId"
        tabindex="-1"
        @keydown="onDialogKeydown"
      >
        <Chrome
          disable-alpha
          :model-value="modelValue"
          class="colorpicker--chrome"
          @update:model-value="updateColor"
        />
      </div>
    </OnClickOutside>
  </div>
</template>

<style scoped lang="scss">
.colorpicker--chrome.vc-chrome {
  @apply shadow-lg absolute bg-n-background z-[9999] border border-n-weak dark:border-n-weak rounded-[8px];

  :deep() {
    .vc-chrome-saturation-wrap {
      @apply rounded-t-[7px];

      .vc-saturation {
        @apply rounded-t-[8px];
      }
    }

    .vc-chrome-body {
      @apply rounded-b-[7px] bg-n-alpha-3;

      .vc-chrome-toggle-btn {
        .vc-chrome-toggle-icon svg {
          @apply [&>path]:fill-n-slate-10 dark:[&>path]:fill-n-slate-10 left-3 relative;
        }
        .vc-chrome-toggle-icon-highlight {
          @apply bg-n-background;
        }
      }
    }

    input,
    .vc-input__input {
      @apply bg-n-background text-n-slate-12 rounded-md shadow-none;
    }

    .vc-input__label {
      @apply text-n-slate-11 dark:text-n-slate-11;
    }
  }
}
</style>
